<script setup>
import { ref, reactive, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { Form, message } from 'ant-design-vue';
import dayjs from 'dayjs';
import { replaceReactiveFormData } from '@/utils/reactiveFormData';

const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: '編輯'
    },
    formItems: {
        type: Array,
        required: true
    },
    initialData: {
        type: Object,
        default: () => ({})
    },
    loading: {
        type: Boolean,
        default: false
    },
    // 統一彈窗寬度
    width: {
        type: [Number, String],
        default: 520
    },
    // 是否顯示遮罩
    mask: {
        type: Boolean,
        default: true
    },
    // 是否關閉過場動畫
    disableAnimation: {
        type: Boolean,
        default: false
    },
    // 內容高度（預設自動依內容撐高）
    bodyHeight: {
        type: String,
        default: 'auto'
    },
    // 內容最大高度（避免過長溢出）
    maxBodyHeight: {
        type: String,
        default: '70vh'
    },
    // 是否將標題放到 Modal 的 title slot，使標題區固定在 body 之外
    useTitleSlot: {
        type: Boolean,
        default: false
    },
    // 與 AntDV 預設一致的頂部距離，確保與「類型管理」對齊
    top: {
        type: String
        // default: '100px'
    },
    // 提升層級以覆蓋大型視窗
    zIndex: {
        type: Number,
        default: 1100
    },
    // 確認/取消按鈕文案（可覆寫）
    okText: {
        type: String,
        default: '確認'
    },
    cancelText: {
        type: String,
        default: '取消'
    },
    // 除錯功能已移除
    debugLog: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:open', 'submit', 'change']);

const modalWrapClass = computed(() =>
    [props.disableAnimation && 'no-anim-modal', 'edit-modal']
        .filter(Boolean)
        .join(' ')
);

const formRef = ref();
// 必須維持同一個 model 參考，供 Ant Design Vue Form 正確同步編輯資料。
const formData = reactive({});
const safeFormItems = computed(() =>
    Array.isArray(props.formItems) ? props.formItems : []
);
// 快取每個 select 的穩定 options，避免每次 render 都是新陣列導致選取失效
const optionsCache = ref({});

// 計算每個表單項目是否應該顯示
const shouldShowItem = (item) => {
    if (!item.showWhen) return true;
    const data = formData;
    const show = item.showWhen(data);
    return show;
};

// 將下拉選項的 value 正規化，避免 '1' 與 1 型別不一致導致選取失效
const normalizeSelectOptions = (item) => {
    const raw =
        typeof item.options === 'function'
            ? item.options()
            : item.options || [];
    const toNumber = (v) => {
        if (typeof v === 'number') return v;
        const n = Number(v);
        return Number.isNaN(n) ? v : n;
    };
    return Array.isArray(raw)
        ? raw.map((opt) => ({
              ...opt,
              value:
                  item.valueType === 'number' ? toNumber(opt.value) : opt.value
          }))
        : [];
};

const rebuildOptionsCache = () => {
    const cache = {};
    (safeFormItems.value || []).forEach((it) => {
        if (it && it.type === 'select') {
            cache[it.key] = normalizeSelectOptions(it);
        }
    });
    optionsCache.value = cache;
};

const coerceBoolean = (val) => {
    if (typeof val === 'boolean') return val;
    if (val === 1 || val === '1') return true;
    if (val === 0 || val === '0') return false;
    return !!val;
};

// 依 formItems 建立初始資料（含類型轉換）
const buildInitialFormData = (source = {}) => {
    const data = {};
    (safeFormItems.value || []).forEach((item) => {
        const raw = source[item.key];
        if (item.type === 'switch') {
            data[item.key] = raw === undefined ? false : coerceBoolean(raw);
        } else if (item.type === 'select') {
            // 選擇器預設為 undefined 才能顯示 placeholder；若上層傳 'all' 視同未選
            data[item.key] =
                raw === undefined || raw === 'all' || raw === ''
                    ? undefined
                    : raw;
        } else if (item.type === 'tags') {
            // 標籤輸入：預設陣列，來源若為字串或空則正規化
            if (Array.isArray(raw)) data[item.key] = raw;
            else if (typeof raw === 'string' && raw.trim() !== '')
                data[item.key] = raw
                    .split(/[,;\s]+/)
                    .map((s) => s.trim())
                    .filter(Boolean);
            else data[item.key] = [];
        } else if (item.type === 'autocomplete') {
            // 顯示鍵使用字串，值鍵使用 source 裡的 valueKey
            data[item.key] = raw === undefined ? '' : raw;
            if (item.valueKey) {
                const rawValue = source[item.valueKey];
                data[item.valueKey] =
                    rawValue === undefined ? undefined : rawValue;
            }
        } else if (item.inputType === 'date') {
            // 日期欄位：將字串轉換為 dayjs 物件
            // 參考課程紀錄的寫法：record.completion_time ? dayjs(record.completion_time) : null
            if (raw === undefined || raw === null || raw === '') {
                data[item.key] = null;
            } else if (dayjs.isDayjs(raw)) {
                // 已經是 dayjs 物件，直接使用
                data[item.key] = raw;
            } else if (typeof raw === 'string' && raw.trim() !== '') {
                // 字串格式，轉換為 dayjs 物件
                const dateObj = dayjs(raw);
                data[item.key] = dateObj.isValid() ? dateObj : null;
            } else {
                data[item.key] = null;
            }
        } else {
            data[item.key] = raw === undefined ? '' : raw;
        }
    });
    return data;
};

// 自動聚焦第一個輸入框（改進版）
const focusFirstInput = () => {
    // 使用雙重 nextTick 確保 DOM 完全渲染
    nextTick(() => {
        nextTick(() => {
            // 尋找第一個可見且非禁用的表單項目
            const firstVisibleItem = safeFormItems.value.find((item) => {
                if (!shouldShowItem(item)) return false;
                // 檢查是否禁用
                if (typeof item.disabled === 'function') {
                    return !item.disabled(formData);
                }
                return !item.disabled;
            });
            if (!firstVisibleItem) return;

            // 根據類型選擇對應的輸入元素
            const inputSelectors = {
                input: 'input:not([type="hidden"])',
                select: '.ant-select-selector',
                textarea: 'textarea',
                date: 'input',
                autocomplete: 'input',
                tags: 'input',
                switch: '.ant-switch'
            };

            const selector =
                inputSelectors[firstVisibleItem.type] ||
                inputSelectors[firstVisibleItem.inputType] ||
                'input:not([type="hidden"])';

            // 使用 key 來定位對應的表單項目
            const formItemContainer = document.querySelector(
                `[data-form-item-key="${firstVisibleItem.key}"]`
            );
            if (!formItemContainer) return;

            const formItem = formItemContainer.querySelector(selector);
            if (formItem) {
                // 勿自動 click select，否則會觸發 values-change／連動清空回填資料
                if (firstVisibleItem.type === 'select') {
                    return;
                }
                if (typeof formItem.focus === 'function') {
                    formItem.focus();
                    if (formItem.select && typeof formItem.select === 'function') {
                        formItem.select();
                    }
                }
            }
        });
    });
};

// 初始化表單資料
const initializeFormData = () => {
    const initialFormData = buildInitialFormData(props.initialData || {});
    console.debug('[BaseModalEdit] 初始化表單資料：', {
        title: props.title,
        initialData: { ...(props.initialData || {}) },
        formItems: safeFormItems.value.map((item) => item.key),
        formData: initialFormData
    });
    replaceReactiveFormData(formData, initialFormData);
    // 重置提交狀態
    isSubmitting.value = false;
};

// 監聽 open 狀態，當彈窗打開時初始化表單
watch(
    () => props.open,
    async (isOpen) => {
        if (isOpen) {
            // 使用 nextTick 確保 initialData 已經更新
            await nextTick();
            // 當彈窗打開時，使用最新的 initialData 初始化表單
            initializeFormData();
            // 自動聚焦第一個輸入框
            focusFirstInput();
        } else {
            // 彈窗關閉時重置提交狀態
            isSubmitting.value = false;
        }
    },
    { immediate: true }
);

// 監聽 initialData 變化，當彈窗打開時同步更新表單
watch(
    () => props.initialData,
    () => {
        if (props.open) {
            // 只在彈窗打開時才更新，避免關閉時不必要的操作
            nextTick(() => {
                initializeFormData();
            });
        }
    },
    { deep: true }
);

// 監聽 formItems 變化，重新初始化（只在彈窗打開時處理）
// 使用 shallow watch 避免深度監聽函數造成循環
// 使用 immediate: false 避免初始化時不必要的執行
watch(
    () => props.formItems,
    () => {
        // 只有在彈窗打開時才重新建立結構
        if (props.open) {
            // 重新建立結構，但盡量保留目前已輸入的值，避免 options 更新時把使用者剛選的值清掉
            const rebuilt = buildInitialFormData(props.initialData || {});
            const next = Object.keys(rebuilt).reduce((acc, key) => {
                acc[key] =
                    formData[key] !== undefined
                        ? formData[key]
                        : rebuilt[key];
                return acc;
            }, {});
            replaceReactiveFormData(formData, next);
        }
        // 表單項目變更時，同步重建 options 快取，提供穩定引用
        rebuildOptionsCache();
    },
    { immediate: false }
);

// 處理表單提交
const handleSubmit = async () => {
    // 防止重複提交
    if (isSubmitting.value || props.loading) {
        return;
    }

    try {
        await formRef.value?.validate?.();
        
        // 設置提交狀態
        isSubmitting.value = true;

        // 將日期欄位的 dayjs 物件轉換為字串格式
        const submitData = { ...formData };
        (safeFormItems.value || []).forEach((item) => {
            if (item.inputType === 'date' && submitData[item.key]) {
                const dateValue = submitData[item.key];
                if (dayjs.isDayjs(dateValue)) {
                    // 支援多種日期格式
                    const format = item.dateFormat || 'YYYY-MM-DD';
                    submitData[item.key] = dateValue.format(format);
                } else if (dateValue && typeof dateValue === 'string') {
                    // 如果已經是字串，嘗試驗證格式
                    const parsed = dayjs(dateValue);
                    if (parsed.isValid()) {
                        const format = item.dateFormat || 'YYYY-MM-DD';
                        submitData[item.key] = parsed.format(format);
                    }
                }
            }
        });
        
        emit('submit', submitData);
    } catch (error) {
        console.error('表單驗證失敗:', error);
        // 重置提交狀態
        isSubmitting.value = false;
        
        // 改善驗證錯誤提示：顯示第一個錯誤欄位的訊息
        if (error?.errorFields && error.errorFields.length > 0) {
            const firstError = error.errorFields[0];
            const errorMessage =
                firstError?.errors?.[0] || '請檢查表單欄位是否正確填寫';
            // 使用 message 組件顯示錯誤給用戶
            message.warning(errorMessage);
            // 滾動到第一個錯誤欄位
            const errorFieldName = firstError?.name?.[0];
            if (errorFieldName) {
                nextTick(() => {
                    const errorElement = document.querySelector(
                        `[data-form-item-key="${errorFieldName}"]`
                    );
                    if (errorElement) {
                        errorElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                });
            }
        } else {
            message.warning('請檢查表單欄位是否正確填寫');
        }
    }
};

// 表單重置功能
const resetForm = () => {
    replaceReactiveFormData(
        formData,
        buildInitialFormData(props.initialData || {})
    );
    formRef.value?.resetFields?.();
    isSubmitting.value = false;
};

// 暴露重置方法給父組件
defineExpose({
    resetForm
});

// 處理取消
const handleCancel = () => {
    emit('update:open', false);
    // 不呼叫 resetFields，避免覆寫布林值
};

// 表單值變更（使用 AntDV 的 values-change）
const handleValuesChange = (changedValues, allValues) => {
    // 直接覆蓋，確保清空後的鍵值會帶出空字串/布林
    Object.assign(formData, allValues);
    // 對外同步通知，供父層在彈窗內聯動（例如：切換分類後重載類型）
    emit('change', changedValues, formData);
};

// 防止重複提交
const isSubmitting = ref(false);

// 鍵盤快捷鍵處理
const handleKeydown = (event) => {
    // 只在彈窗打開時處理
    if (!props.open) return;

    // ESC 鍵：關閉彈窗
    if (event.key === 'Escape' && !props.loading && !isSubmitting.value) {
        handleCancel();
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    // Enter + Ctrl/Cmd：提交表單（避免在 textarea 中觸發）
    if (
        event.key === 'Enter' &&
        (event.ctrlKey || event.metaKey) &&
        !props.loading &&
        !isSubmitting.value &&
        event.target.tagName !== 'TEXTAREA'
    ) {
        handleSubmit();
        event.preventDefault();
        event.stopPropagation();
        return;
    }
};

// 監聽鍵盤事件（只在彈窗打開時）
watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            window.addEventListener('keydown', handleKeydown);
        } else {
            window.removeEventListener('keydown', handleKeydown);
        }
    }
);

// 組件卸載時清理
onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <a-modal
        :open="open"
        :title="title"
        :wrap-class-name="modalWrapClass"
        :confirm-loading="loading"
        @ok="handleSubmit"
        @cancel="handleCancel"
        :width="width"
        :mask="mask"
        :mask-closable="true"
        :centered="!top"
        :transition-name="disableAnimation ? '' : undefined"
        :mask-transition-name="disableAnimation ? '' : undefined"
        :body-style="{
            height: bodyHeight,
            maxHeight: maxBodyHeight,
            overflowY: 'auto'
        }"
        :style="top ? { top } : undefined"
        :zIndex="zIndex"
        :footer="null"
    >
        <a-form
            ref="formRef"
            layout="vertical"
            class="base-edit-form"
            :model="formData"
            @values-change="handleValuesChange"
        >
            <template v-for="item in safeFormItems" :key="item.key">
                <a-form-item
                    v-if="shouldShowItem(item)"
                    :label="item.type === 'switch' ? undefined : item.label"
                    :name="item.key"
                    :rules="item.rules || []"
                    class="base-edit-form__item"
                    :class="{
                        'base-edit-form__item--half': Number(item.span) === 1,
                        'base-edit-form__item--switch': item.type === 'switch'
                    }"
                    :data-form-item-key="item.key"
                >
                    <a-date-picker
                        v-if="item.inputType === 'date'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請選擇日期'"
                        format="YYYY-MM-DD"
                        class="w-full"
                        allow-clear
                        :get-popup-container="
                            (node) =>
                                node?.closest('.ant-modal-root') ||
                                node?.closest('.ant-modal-wrap') ||
                                node?.closest('.ant-modal') ||
                                node?.parentNode
                        "
                        :popup-style="{ zIndex: 3000 }"
                    />
                    <a-input
                        v-else-if="!item.type || item.type === 'input'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請輸入'"
                        :type="item.inputType || 'text'"
                        :disabled="
                            typeof item.disabled === 'function'
                                ? item.disabled(formData)
                                : !!item.disabled
                        "
                        :allow-clear="
                            !(typeof item.disabled === 'function'
                                ? item.disabled(formData)
                                : !!item.disabled)
                        "
                        size="middle"
                        class="w-full"
                    />
                    <a-select
                        v-else-if="item.type === 'select'"
                        :key="item.renderKey || item.key"
                        :value="formData[item.key]"
                        class="w-full"
                        @update:value="
                            (val) => {
                                formData[item.key] = val;
                                handleValuesChange(
                                    { [item.key]: val },
                                    formData
                                );
                            }
                        "
                        :placeholder="item.placeholder || '請選擇'"
                        :options="normalizeSelectOptions(item)"
                        :field-names="{
                            label: 'label',
                            value: 'value'
                        }"
                        :disabled="
                            typeof item.disabled === 'function'
                                ? item.disabled(formData)
                                : !!item.disabled
                        "
                        allow-clear
                        size="middle"
                        :get-popup-container="
                            (node) =>
                                node?.closest('.ant-modal-root') ||
                                node?.closest('.ant-modal-wrap') ||
                                node?.closest('.ant-modal') ||
                                node?.parentNode
                        "
                        :dropdown-style="{ zIndex: 3000 }"
                        :virtual="false"
                        :dropdownMatchSelectWidth="true"
                        @change="
                            (val) => {
                                if (
                                    item.valueType === 'number' &&
                                    typeof val === 'string'
                                ) {
                                    const n = Number(val);
                                    formData[item.key] = Number.isNaN(n)
                                        ? undefined
                                        : n;
                                }
                            }
                        "
                    />
                    <a-select
                        v-else-if="item.type === 'tags'"
                        v-model:value="formData[item.key]"
                        mode="tags"
                        class="w-full"
                        :token-separators="[',', ';']"
                        :placeholder="
                            item.placeholder || '輸入後按 Enter 新增'
                        "
                        allow-clear
                        :get-popup-container="
                            (node) =>
                                node?.closest('.ant-modal-root') ||
                                node?.closest('.ant-modal-wrap') ||
                                node?.closest('.ant-modal') ||
                                node?.parentNode
                        "
                    />
                    <a-auto-complete
                        v-else-if="item.type === 'autocomplete'"
                        v-model:value="formData[item.key]"
                        class="w-full"
                        :options="
                            typeof item.options === 'function'
                                ? item.options()
                                : item.options || []
                        "
                        :placeholder="
                            item.placeholder || '請輸入關鍵字搜尋'
                        "
                        :filter-option="
                            item.filterOption === false
                                ? false
                                : item.filterOption ||
                                  ((inputValue, option) => {
                                      const input = String(
                                          inputValue || ''
                                      ).toLowerCase();
                                      const label = String(
                                          option.label || ''
                                      ).toLowerCase();
                                      const value = String(
                                          option.value || ''
                                      ).toLowerCase();
                                      return (
                                          label.includes(input) ||
                                          value.includes(input)
                                      );
                                  })
                        "
                        :loading="item.loading === true"
                        allow-clear
                        @search="
                            item.onSearch
                                ? (val) => item.onSearch(val, item.key)
                                : undefined
                        "
                        :get-popup-container="
                            (node) =>
                                node?.closest('.ant-modal-root') ||
                                node?.closest('.ant-modal-wrap') ||
                                node?.closest('.ant-modal') ||
                                node?.parentNode
                        "
                        :dropdown-style="{ zIndex: 3000 }"
                        @select="
                            (value, option) => {
                                if (item.valueKey)
                                    formData[item.valueKey] = option.value;
                                formData[item.key] =
                                    option.label ?? String(option.value);
                            }
                        "
                        @change="
                            (value) => {
                                if (!value && item.valueKey)
                                    formData[item.valueKey] = undefined;
                            }
                        "
                    />
                    <div v-else-if="item.type === 'switch'" class="base-edit-form__switch">
                        <span class="base-edit-form__switch-label">{{ item.label }}</span>
                        <a-switch
                            v-model:checked="formData[item.key]"
                            :disabled="
                                typeof item.disabled === 'function'
                                    ? item.disabled(formData)
                                    : !!item.disabled
                            "
                        />
                    </div>
                    <a-textarea
                        v-else-if="item.type === 'textarea'"
                        v-model:value="formData[item.key]"
                        :placeholder="item.placeholder || '請輸入'"
                        :rows="item.rows || 3"
                        allow-clear
                        size="middle"
                        class="w-full"
                    />
                </a-form-item>
            </template>
        </a-form>

        <div class="base-edit-form__footer">
            <a-button @click="handleCancel" :disabled="loading || isSubmitting">
                {{ cancelText }}
            </a-button>
            <a-button
                type="primary"
                @click="handleSubmit"
                :loading="loading || isSubmitting"
                :disabled="isSubmitting"
            >
                {{ okText }}
            </a-button>
        </div>
    </a-modal>
</template>

<style scoped>
.base-edit-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 12px;
    align-items: start;
}

.base-edit-form__item {
    grid-column: 1 / -1;
    margin-bottom: 12px !important;
}

.base-edit-form__item--half {
    grid-column: span 1;
}

.base-edit-form :deep(.ant-form-item-label) {
    padding-bottom: 4px;
}

.base-edit-form :deep(.ant-form-item-label > label) {
    font-size: 13px;
    height: auto;
}

.base-edit-form__switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 32px;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid #e8edf0;
    background: #f8fafc;
}

.base-edit-form__switch-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
}

.base-edit-form__item--switch :deep(.ant-form-item-control-input-content) {
    width: 100%;
}

.base-edit-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 4px;
    padding-top: 14px;
    border-top: 1px solid #eef2f0;
}

@media (max-width: 560px) {
    .base-edit-form {
        grid-template-columns: 1fr;
    }

    .base-edit-form__item--half {
        grid-column: 1 / -1;
    }
}
</style>
